<template>
  <v-card id="service-dish-list">
    <v-card-text class="pb-0">
      <p class="text--secondary mb-3">
        Подарочный товар и приборы — бесплатные позиции кассы. Руками их не заводят: синхронизация
        берёт всё, что лежит в категориях, выбранных в «Настройки системы → Синхронизация меню».
        Здесь позиции сверяют с кассой и загружают им картинки — без картинки подарок в корзине пустой.
      </p>

      <div class="d-flex align-center flex-wrap pb-3">
        <v-btn
          color="primary"
          class="me-3 mb-2"
          :loading="syncing"
          :disabled="hasNotPermission('Permissions.ServiceDishUpdate')"
          @click="sync"
        >
          <v-icon
            size="18"
            class="me-1"
          >
            {{ icons.mdiSync }}
          </v-icon>
          <span>Синхронизировать с кассой</span>
        </v-btn>
        <v-btn
          text
          color="secondary"
          class="mb-2"
          :to="{ name: 'system-settings' }"
        >
          <v-icon
            size="18"
            class="me-1"
          >
            {{ icons.mdiCogOutline }}
          </v-icon>
          <span>Категории — в настройках синхронизации меню</span>
        </v-btn>
      </div>

      <v-alert
        v-if="lastSync"
        :type="lastSync.skipped ? 'warning' : 'success'"
        text
        dense
        dismissible
      >
        <template v-if="lastSync.skipped">
          Категории подарочного товара и приборов не выбраны — синхронизировать нечего.
          Выберите их в «Настройки системы → Синхронизация меню».
        </template>
        <template v-else>
          Готово: можно выбирать {{ lastSync.available }}.
          <span v-if="lastSync.added.length">Новые: {{ lastSync.added.join(', ') }}.</span>
          <span v-if="lastSync.becameUnavailable.length">
            Стали недоступны: {{ lastSync.becameUnavailable.join(', ') }}.
          </span>
        </template>
      </v-alert>

      <v-alert
        v-if="error"
        type="error"
        text
        dense
        dismissible
      >
        {{ error }}
      </v-alert>
    </v-card-text>

    <v-tabs
      v-model="tab"
      show-arrows
    >
      <v-tab
        v-for="type in types"
        :key="type.id"
      >
        <v-icon
          size="20"
          class="me-2"
        >
          {{ type.icon }}
        </v-icon>
        <span>{{ type.name }} · {{ count(type.id) }}</span>
      </v-tab>
    </v-tabs>

    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      no-data-text="Позиций нет — выберите категории в настройках и синхронизируйте"
      hide-default-footer
      disable-pagination
    >
      <template #[`item.image`]="{ item }">
        <v-avatar
          size="48"
          rounded
          class="service-dish-image my-2"
        >
          <v-img
            v-if="item.image"
            :src="item.image"
          ></v-img>
          <v-icon v-else>
            {{ icons.mdiImageOffOutline }}
          </v-icon>
        </v-avatar>
      </template>
      <template #[`item.name`]="{ item }">
        <div class="font-weight-medium">
          {{ item.name }}
        </div>
        <div class="text-caption text--secondary">
          код {{ item.code || '—' }} · r_keeper {{ item.rkId }}
        </div>
      </template>
      <template #[`item.ids`]="{ item }">
        <div class="text-caption text-no-wrap">
          GUID r_keeper: {{ item.rkGuid || '—' }}
        </div>
        <div class="text-caption text-no-wrap">
          id Delivery: {{ item.deliveryGuid || '—' }}
        </div>
      </template>
      <template #[`item.price`]="{ item }">
        <span class="text-no-wrap">{{ item.price }} сом</span>
      </template>
      <template #[`item.isAvailable`]="{ item }">
        <v-chip
          small
          outlined
          :color="item.isAvailable ? 'success' : 'warning'"
        >
          {{ status(item) }}
        </v-chip>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn
          small
          outlined
          :loading="uploadingId === item.id"
          :disabled="hasNotPermission('Permissions.ServiceDishUpdate')"
          @click="pickImage(item)"
        >
          <v-icon
            size="18"
            class="me-1"
          >
            {{ icons.mdiImageEditOutline }}
          </v-icon>
          <span>{{ item.image ? 'Заменить' : 'Загрузить' }}</span>
        </v-btn>
      </template>
    </v-data-table>

    <input
      ref="file"
      type="file"
      accept="image/*"
      class="d-none"
      @change="upload"
    >
  </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import {
  mdiSync,
  mdiCogOutline,
  mdiImageOffOutline,
  mdiImageEditOutline,
  mdiGiftOutline,
  mdiSilverwareForkKnife,
} from '@mdi/js'
import { hasPermission } from '@/helpers/index'

export default {
  name: 'ServiceDishList',
  data: () => ({
    icons: {
      mdiSync,
      mdiCogOutline,
      mdiImageOffOutline,
      mdiImageEditOutline,
    },
    tab: 0,
    types: [
      { id: 'Present', name: 'Подарочный товар', icon: mdiGiftOutline },
      { id: 'Appliance', name: 'Приборы', icon: mdiSilverwareForkKnife },
    ],
    picked: null,
  }),
  computed: {
    ...mapState('serviceDish', ['list', 'loading', 'syncing', 'uploadingId', 'lastSync', 'error']),
    items() {
      const type = this.types[this.tab]?.id

      return this.list.filter(x => x.type === type)
    },
    headers() {
      return [
        {
          text: '', value: 'image', sortable: false, width: 64,
        },
        { text: 'Позиция', value: 'name' },
        { text: 'Номера', value: 'ids', sortable: false },
        { text: 'Цена', value: 'price' },
        { text: 'Состояние', value: 'isAvailable' },
        {
          text: 'Картинка', value: 'actions', align: 'center', sortable: false,
        },
      ]
    },
  },
  async created() {
    document.title = 'Подарочный товар'
    await this.fetchList()
  },
  methods: {
    ...mapActions('serviceDish', ['fetchList', 'sync', 'uploadImage']),
    hasNotPermission(value) {
      return !hasPermission(value)
    },
    count(type) {
      return this.list.filter(x => x.type === type).length
    },

    status(item) {
      if (item.isAvailable) return 'можно выбирать'
      if (item.price > 0) return 'платная — не годится'

      return 'нет в Delivery'
    },
    pickImage(item) {
      this.picked = item
      this.$refs.file.value = ''
      this.$refs.file.click()
    },
    async upload(event) {
      const [file] = event.target.files

      if (!file || !this.picked) return

      await this.uploadImage({ id: this.picked.id, file })
      this.picked = null
    },
  },
}
</script>

<style lang="scss" scoped>
#service-dish-list {
  .service-dish-image {
    background: rgba(94, 86, 105, 0.08);
  }
}
</style>
